/*
 * Copyright (c) 2023. American Express Company
 */

// theirs
import React from 'react';
import { Alert, FieldControl, Hint, Input, Label } from '@americanexpress/dls-react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';

// ours
import { BasicInputViewModel } from 'src/types/view-models/basic-input.view.model';
import Selectors from 'src/utils/selectors';

export default function BasicInput({
    id,
    label,
    placeholder,
    hint,
    className,
    hideLabel = false,
    onClick
}: BasicInputViewModel): JSX.Element {
    const [field, meta]: [
        FieldInputProps<number | string>,
        FieldMetaProps<number | string>,
        FieldHelperProps<number | string>
    ] = useField(id);
    const hasError: boolean = meta.touched && meta.error?.length > 0;
    // DLS requires we associate Alert with Input in this particular way
    const alertId: string = `${id}Alert`;
    const describedBy: { describedBy?: string } = hasError ? { describedBy: alertId } : {};

    return (
        <FieldControl status={hasError ? 'error' : 'default'} data-cy={`${id}BasicInput`} className={className}>
            {!hideLabel && label && <Label htmlFor={id}>{label}</Label>}
            {hint && <Hint htmlFor={id}>{hint}</Hint>}
            <Input
                theme={hideLabel ? { marginTop: 0 } : {}}
                id={id}
                placeholder={placeholder}
                onClick={onClick}
                aria-label={hideLabel ? label : null}
                data-cy={`${id}${Selectors.SpendSmart.BASIC_INPUT_FIELD}`}
                {...describedBy}
                {...field}
            />
            {hasError && (
                <Alert id={alertId} data-cy={`${id}${Selectors.SpendSmart.BASIC_INPUT_ERROR}`}>
                    {meta.error}
                </Alert>
            )}
        </FieldControl>
    );
}
